import { inferTableStructure } from '../../utils/tableInference';

// Role definitions per chart type
// Each role: { role, label, types (accepted column types), required }
export const CHART_ROLE_MAP = {
  'vertical-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'colorBy', label: 'Color by (optional)', types: ['string'], required: false },
  ],
  'horizontal-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'colorBy', label: 'Color by (optional)', types: ['string'], required: false },
  ],
  'grouped-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'series', label: 'Group by', types: ['string'], required: true },
  ],
  'horizontal-grouped-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'series', label: 'Group by', types: ['string'], required: true },
  ],
  'stacked-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'series', label: 'Stack by', types: ['string'], required: true },
  ],
  'horizontal-stacked-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'series', label: 'Stack by', types: ['string'], required: true },
  ],
  'line-chart': [
    { role: 'x', label: 'X-axis', types: ['number'], required: true },
    { role: 'y', label: 'Y-axis', types: ['number'], required: true },
    { role: 'series', label: 'Split lines by', types: ['string'], required: false },
  ],
  'scatter-plot': [
    { role: 'x', label: 'X coordinate', types: ['number'], required: true },
    { role: 'y', label: 'Y coordinate', types: ['number'], required: true },
    { role: 'category', label: 'Color by', types: ['string'], required: false },
    { role: 'size', label: 'Size by', types: ['number'], required: false },
  ],
  'histogram': [
    { role: 'value', label: 'Values to bin', types: ['number'], required: true },
  ],
  'box-plot': [
    { role: 'category', label: 'Group name', types: ['string'], required: true },
    { role: 'value', label: 'Measured value', types: ['number'], required: true },
  ],
  'lollipop': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column', types: ['number'], required: true },
    { role: 'colorBy', label: 'Color by (optional)', types: ['string'], required: false },
  ],
  'diverging-bar': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'value', label: 'Numeric column (+/-)', types: ['number'], required: true },
    { role: 'colorBy', label: 'Color by (optional)', types: ['string'], required: false },
  ],
  'heatmap': [
    { role: 'x', label: 'Column labels', types: ['string'], required: true },
    { role: 'y', label: 'Row labels', types: ['string'], required: true },
    { role: 'value', label: 'Cell value', types: ['number'], required: true },
  ],
  'combo': [
    { role: 'category', label: 'Label column', types: ['string'], required: true },
    { role: 'barValue', label: 'Bar values (left axis)', types: ['number'], required: true },
    { role: 'lineValue', label: 'Line values (right axis)', types: ['number'], required: true },
  ],
  'radar': [
    { role: 'series', label: 'Name / label column', types: ['string'], required: true },
  ],
};

// Generic roles when no chart is selected
export const GENERIC_ROLES = [
  { role: 'x', label: 'X axis', types: ['string', 'number'], required: false },
  { role: 'y', label: 'Y axis', types: ['number'], required: false },
  { role: 'category', label: 'Category', types: ['string'], required: false },
  { role: 'series', label: 'Series', types: ['string'], required: false },
];

const CATEGORY_NAME_PATTERNS = ['model', 'name', 'label', 'category', 'group', 'type', 'class'];

/**
 * Compute smart guesses for column-to-role mapping.
 */
export function computeSmartGuess(columns, chartId, rows = []) {
  const roles = chartId ? (CHART_ROLE_MAP[chartId] || GENERIC_ROLES) : GENERIC_ROLES;
  const inferred = inferTableStructure(rows, columns, chartId);
  if (inferred.suggestedMapping) {
    return inferred.suggestedMapping;
  }
  const mapping = {};
  const assignedColumns = new Set();
  const assignedRoles = new Set();

  const stringCols = columns.filter(c => c.type === 'string');
  const numberCols = columns.filter(c => c.type === 'number');

  // Simple case: exactly 1 string + 1 numeric -> auto-map
  if (stringCols.length === 1 && numberCols.length === 1) {
    const catRole = roles.find(r => r.role === 'category' || r.role === 'x');
    const valRole = roles.find(r => r.role === 'value' || r.role === 'y');
    if (catRole) {
      mapping[catRole.role] = stringCols[0].name;
      assignedColumns.add(stringCols[0].name);
      assignedRoles.add(catRole.role);
    }
    if (valRole) {
      mapping[valRole.role] = numberCols[0].name;
      assignedColumns.add(numberCols[0].name);
      assignedRoles.add(valRole.role);
    }
    return mapping;
  }

  // Pass 1: Name-based matching for category-like columns
  for (const col of columns) {
    if (assignedColumns.has(col.name)) continue;
    const lowerName = col.name.toLowerCase().trim();
    if (CATEGORY_NAME_PATTERNS.some(p => lowerName === p || lowerName.includes(p))) {
      const catRole = roles.find(r =>
        (r.role === 'category' || r.role === 'x') &&
        !assignedRoles.has(r.role) &&
        r.types.includes(col.type)
      );
      if (catRole) {
        mapping[catRole.role] = col.name;
        assignedColumns.add(col.name);
        assignedRoles.add(catRole.role);
      }
    }
  }

  // Pass 2: Type-based matching for remaining roles (prefer matching type, fall back to any)
  for (const roleDef of roles) {
    if (assignedRoles.has(roleDef.role)) continue;
    const preferred = columns.find(c =>
      !assignedColumns.has(c.name) &&
      roleDef.types.includes(c.type)
    );
    if (preferred) {
      mapping[roleDef.role] = preferred.name;
      assignedColumns.add(preferred.name);
      assignedRoles.add(roleDef.role);
    } else if (roleDef.required) {
      const fallback = columns.find(c => !assignedColumns.has(c.name));
      if (fallback) {
        mapping[roleDef.role] = fallback.name;
        assignedColumns.add(fallback.name);
        assignedRoles.add(roleDef.role);
      }
    }
  }

  return mapping;
}
